import React from "react";
import {autorun} from "mobx";
import {observer} from "mobx-react";

import "./coordinate.scss";

import { Icon } from "../icon/icon";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Extent from "@arcgis/core/geometry/Extent";
import {IAppStore, withStore} from "../../stores/abstract-app-store";

interface CoordinateProps {
    store?: IAppStore; // this is injected by withStore
}

@observer
class CoordinateBase extends React.Component<CoordinateProps, any> {
    autorunDisposers: any[] = [];
    elevationLayerIndex = -1;
    elevationSampler: Promise<__esri.ElevationSampler> | null = null;
    watchHandles: any[] = [];
    widgetKey = "coordinate";
    cursorTimeout = 250;
    cursorTimeoutId: number | null = null;
    constructor(props: CoordinateProps) {
        super(props);
        this.state = {
            isLoading: true,
            coordinate: null,
            titleElevationLayer: null
        };
    }
    resetCursorTimer(): void {
        if (this.cursorTimeoutId) {
            window.clearTimeout(this.cursorTimeoutId);
        }
    }
    loadElevationLayer(view: __esri.MapView | __esri.SceneView | undefined): void {
        if (!view) {
            return;
        }
        if (view.map.ground.layers.length > 0) {
            // Suche Layer mit der id "dom", ansonsten erster Layer in der ground Liste
            this.elevationLayerIndex =
                Math.max(0, view.map.ground.layers.findIndex((e)=> e.id==="dom" ));
            if (view.map.ground.layers.getItemAt(this.elevationLayerIndex).title
                !== this.state.titleElevationLayer) {
                this.updateElevationSampler();
            }
        }
    }
    componentDidMount(): void {
        const {store} = this.props;

        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (store?.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }

            if (!store?.mapStore.view) {
                return;
            }
            this.setState({
                coordinate: null,
                elevation: null
            });
            this.watchHandles.push(
                store?.mapStore.view.watch("stationary",
                    () =>  {
                        this.resetCursorTimer();
                        this.setState({
                            coordinate: null,
                            elevation: null
                        });
                    })
            );
            this.watchHandles.push(
                reactiveUtils.when(
                    () => store?.mapStore.view?.stationary === true,
                    () => {
                        if (this.elevationLayerIndex >= 0) {
                            this.updateElevationSampler();
                        }
                    })
            );
            this.loadElevationLayer(store?.mapStore.view);
            this.watchHandles.push(
                store?.mapStore.view.watch("map.ground.layers.length",
                    () =>  {
                        this.loadElevationLayer(store?.mapStore.view);
                    })
            );
            this.watchHandles.push(
                store?.mapStore.view.on("pointer-move", (evt) => {
                    this.resetCursorTimer();
                    this.setState({
                        coordinate: store?.mapStore.view?.toMap({ x: evt.x, y: evt.y }),
                        elevation: store?.mapStore.view?.map.ground? -1: null
                    });
                    const mapPoint = store?.mapStore.view?.toMap(evt);
                    if (this.elevationSampler && mapPoint) {
                        this.elevationSampler.then((elevationSampler)=> {
                            const mapPointZ = elevationSampler.queryElevation(mapPoint) as __esri.Point;
                            this.setState({elevation: mapPointZ.z});
                        });
                    }

                })
            );
            this.watchHandles.push(
                store?.mapStore.view.on("pointer-leave", () => {
                    this.resetCursorTimer();
                    this.setState({
                        coordinate: null,
                        elevation: store?.mapStore.view?.map.ground? -1: null
                    });
                })
            );
            this.setState({
                isLoading: false
            });
            store?.uiStore.addWidget(this.widgetKey, this);

            reaction.dispose();
        }));
    }
    updateElevationSampler() {
        const {store} = this.props;
        if (!store?.mapStore.view) {
            return;
        }
        this.setState({
            titleElevationLayer: store?.mapStore.view.map.ground.layers.getItemAt(this.elevationLayerIndex).title
        });
        if(store?.mapStore.view.extent){
            // Extent als kopie übergeben damit veränderungen innerhalb des ElevationSampler keinen Einfluss auf die
            // Karte haben. Ansonsten funktioniert die Druckfunktion in der Grenzregion nicht korrekt.
            this.elevationSampler =
                store?.mapStore.view.map.ground.layers.getItemAt(this.elevationLayerIndex)
                    .createElevationSampler(new Extent(store?.mapStore.view.extent.toJSON()));
        }else{
            this.elevationSampler = null;
        }
    }

    componentWillUnmount() {
        const {store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }
    render(): JSX.Element | null {
        const {coordinate, elevation, titleElevationLayer} = this.state;
        let coords = "";
        let elevationElement: JSX.Element | null = null;
        let className: string;
        if(coordinate){
            className ="show";
            coords =
                "E " +
                new Intl.NumberFormat("de-CH").format(Math.round(coordinate.x)) +
                " / N " +
                new Intl.NumberFormat("de-CH").format(Math.round(coordinate.y));
            if (elevation !== null) {
                if (elevation === -1) {
                    elevationElement = <span className={"geo-elevation"}>
                        Höhe: <span className={"value"}>
                            <Icon name={"recycle"} className={"geo-rotating"} /> m.ü.M
                        </span>
                    </span>;
                } else {
                    // coords += new Intl.NumberFormat("de-CH").format(Math.round(elevation)) + " m.ü.M";
                    elevationElement = <span className={"geo-elevation"}>
                        Höhe: <span className={"value"}>{ (elevation === 0)? "?":
                            new Intl.NumberFormat("de-CH").format(Math.round(elevation))
                        } m.ü.M ({titleElevationLayer})</span></span>;
                }
            }
        } else {
            className ="hide";
        }
        return (
            <div className={"geo-coordinate-elevation "+className}>
                <span className={"geo-coordinate"}>{coords}</span>{elevationElement}</div>
        );
    }
}

export const Coordinate = withStore(CoordinateBase);
